'use client';

import React, { useState, useEffect } from 'react';
import { X, Maximize2, Minimize2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion, AnimatePresence } from 'framer-motion';

interface TerminalDocumentProps {
  title: string;
  content: string;
  onClose: () => void;
  showLineNumbers?: boolean;
}

export function TerminalDocument({ 
  title, 
  content, 
  onClose,
  showLineNumbers = false 
}: TerminalDocumentProps) {
  const [isMaximized, setIsMaximized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const lines = content.split('\n');

  useEffect(() => {
    // Simulate terminal boot-up with flicker effect
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`relative flex flex-col ${isMaximized ? 'h-screen w-screen' : 'h-[90vh] w-full'} glass-terminal`}>
      {/* Terminal Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-cyan-400/10 bg-black/20 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/60 shadow-sm cursor-pointer hover:bg-red-500/80" onClick={onClose}></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500/60 shadow-sm"></div>
            <div className="w-3 h-3 rounded-full bg-green-500/60 shadow-sm"></div>
          </div>
          <div className="text-sm text-cyan-300/80 font-mono">
            {title}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMaximized(!isMaximized)}
            className="p-1 hover:bg-white/10 rounded transition-colors"
          >
            {isMaximized ? (
              <Minimize2 className="w-4 h-4 text-cyan-300/60" />
            ) : (
              <Maximize2 className="w-4 h-4 text-cyan-300/60" />
            )}
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-4 h-4 text-cyan-300/60" />
          </button>
        </div>
      </div>

      {/* Terminal Body with Scanlines */}
      <div className="relative flex-1 bg-black/30 overflow-hidden">
        {/* Scanline Effect */}
        <div className="scanlines pointer-events-none absolute inset-0 z-10"></div>
        
        {/* Terminal Content with Flicker Animation */}
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: [0, 1, 0.92, 1],
                filter: ['brightness(0.7) blur(0.5px)', 'brightness(1) blur(0px)']
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center justify-center h-full"
            >
              <div className="text-cyan-300 terminal-glow font-mono">
                INITIALIZING...
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0, scale: 0.985, filter: 'brightness(0.7) blur(0.5px)' }}
              animate={{ 
                opacity: [0, 1, 0.92, 1], 
                scale: 1, 
                filter: ['brightness(0.7) blur(0.5px)', 'brightness(1) blur(0px)']
              }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="h-full"
            >
              <ScrollArea className="h-full">
                <div className="p-6 font-mono text-sm">
                  {lines.map((line, i) => (
                    <motion.div 
                      key={i} 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.001, duration: 0.1 }}
                      className="flex hover:bg-cyan-400/5 transition-colors"
                    >
                      {showLineNumbers && (
                        <span className="text-cyan-600/50 mr-4 select-none min-w-[3ch] text-right">
                          {String(i + 1).padStart(3, '0')}
                        </span>
                      )}
                      <span className="text-cyan-300 terminal-glow whitespace-pre-wrap">
                        {line || '\u00A0'}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </ScrollArea>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Terminal Footer */}
      <div className="px-4 py-2 border-t border-cyan-400/10 bg-black/20 backdrop-blur-xl">
        <div className="flex items-center justify-between text-xs font-mono text-cyan-400/60">
          <span>Ready</span>
          <span>{lines.length} lines</span>
        </div>
      </div>

      <style jsx>{`
        .glass-terminal {
          background: linear-gradient(135deg, 
            rgba(26, 31, 44, 0.4) 0%, 
            rgba(15, 20, 30, 0.5) 100%);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(0, 191, 255, 0.15);
          border-radius: 1rem;
          box-shadow: 
            0 8px 32px 0 rgba(15, 20, 25, 0.37),
            0 0 40px rgba(0, 191, 255, 0.05),
            inset 0 0 20px rgba(0, 191, 255, 0.02);
        }

        .terminal-glow {
          text-shadow: 
            0 0 3px rgba(0, 191, 255, 0.5),
            0 0 10px rgba(0, 191, 255, 0.2);
        }

        .scanlines {
          background: linear-gradient(
            to bottom,
            transparent 50%,
            rgba(0, 191, 255, 0.03) 50%
          );
          background-size: 100% 4px;
          animation: scanlines 8s linear infinite;
        }

        @keyframes scanlines {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 0 10px;
          }
        }
      `}</style>
    </div>
  );
}

export default TerminalDocument;